import { dbg, makeLogger } from "@gazebo/utils";
import { BlockStore } from "@store/BlockStore";
import { NoteStore } from "@store/NoteStore";
import { SearchStore } from "@store/SearchStore";
import isHotkey from "is-hotkey";
import isEqual from "lodash/isEqual";
import { action } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createEditor, Descendant, Editor, Node, NodeEntry, Transforms } from "slate";
import { withHistory } from "slate-history";
import {
    Editable, ReactEditor, RenderElementProps,
    RenderLeafProps,
    Slate,
    withReact
} from "slate-react";
import { Autocomplete, AutocompleteUI } from "./autocomplete";
import { withBlocks } from "./blocks";
import { Block, InternalLink, Leaf, Paragraph, Title } from "./components";
import { withInternalLinks } from "./internalLinks";
import { withParagraphs } from "./paragraphs";
import { withTitles, withTitlesAsBlockStarters } from "./titles";
import { IBlock, isBlock, isInternalLink } from "./types";
import { ActivityProvider, useActivitySignal } from "./useIsActive";

const renderElement = (props: RenderElementProps): JSX.Element => {
    switch (props.element.type) {
        case "title":
            return <Title {...props} />;
        case "internalLink":
            return <InternalLink {...props} />;
        case "paragraph":
            return <Paragraph {...props} />;
        case "block":
            return <Block {...props} />;
        default:
            // @ts-ignore
            throw new Error(`missing: ${props.element.type}`);
    }
};

const renderLeaf = (props: RenderLeafProps): JSX.Element => {
    return <Leaf {...props} />;
};

const isSaveHotkey = isHotkey("mod+s");
const isTestHotkey = isHotkey("mod+d");

// const useSelection = () => {
//     const [selection, setSelection] = useState(editor.selection)

//     useEffect(() => {
//         setSelection(editor.selection)
//     }, [setSelection, editor.selection])

//     return selection
// }

const hasDifferentPaths = (a: NodeEntry, b: NodeEntry): boolean => {
    return !isEqual(a[1], b[1])
}

const logger = makeLogger('gardeneditor')

export const GardenEditor: React.FC<{
    value?: Descendant[];
    setValue?: (x: Descendant[]) => void;
    note?: NoteStore;
    searchStore: SearchStore
}> = observer(({ note, searchStore }) => {
    const editor = useMemo(
        () =>
            dbg("creating editor:",
                withTitlesAsBlockStarters(
                    withTitles(
                        withInternalLinks(
                            withParagraphs(
                                withBlocks(
                                    withHistory(withReact(createEditor()))
                                )
                            )
                        )
                    )
                )),
        []
    );

    useEffect(() => {
        note?.slate.refresh()
    }, [note])

    const [value, setValue] = useState<any>(null)
    const validInputs = (!!note) || (!!value && !!setValue)

    if (!validInputs) {
        throw Error('invalid inputs!')
    }

    const autoComplete = useMemo(() => new Autocomplete(editor, searchStore), [editor, searchStore])

    const [focused, setFocused_] = useState<NodeEntry<Node> | null>(null)

    const setFocused = useCallback((newValue: NodeEntry<Node> | null) => {
        setFocused_((prevState: NodeEntry<Node> | null) => {
            if (prevState) {
                // Unfocus the previous state.
                if (!newValue || hasDifferentPaths(newValue, prevState)) {
                    Transforms.setNodes(editor, { focused: undefined }, { at: prevState[1] })
                }
            }

            // Focus the new state.
            if (newValue) {
                Transforms.setNodes(editor, { focused: true }, { at: newValue[1] })
            }

            return newValue
        })

    }, [setFocused_, editor])

    const [editorWasFocused, setEditorWasFocused_] = useState(ReactEditor.isFocused(editor))
    const editorIsFocused = ReactEditor.isFocused(editor)

    useEffect(() => {
        setEditorWasFocused_((previous: boolean) => {
            if (previous !== editorIsFocused) {
                if (editorIsFocused) {
                    ReactEditor.toDOMNode(editor, editor).scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                }
            }
            return editorIsFocused
        })
    }, [setEditorWasFocused_, editor, editorIsFocused])

    useEffect(() => {
        const changed: BlockStore[] = note?.slate.blocksWithChanges || []

        const [match] = Editor.nodes(editor, {
            match: n => isBlock(n)
        })

        const id = match ? (match[0] as IBlock).blockID : null

        changed.forEach(x => {
            if (id && x.id === id) {
                editor.selection = null
            }
            x.optimism.refresh()
        })
    }, [note?.slate.blocksWithChanges, editor, editor.selection])

    useEffect(() => {
        // const el = ref.current
        const { selection } = editor

        // if (!el) {
        //     return
        // }

        if (!selection ||
            !ReactEditor.isFocused(editor)
            // Editor.string(editor, selection) === ''
        ) {
            setFocused(null)
            return
        }

        const [m] = Editor.nodes(editor, { match: n => isInternalLink(n) })

        if (m) {
            setFocused(m)
        } else {
            setFocused(null)
        }

        // const domSelection = window.getSelection()

        // if (!domSelection) {
        //     console.error('invalid dom selection')
        //     return;
        // }

        // const domRange = domSelection.getRangeAt(0)
        // const rect = domRange.getBoundingClientRect()

        // el.style.opacity = '1'
        // el.style.top = `${rect.top + window.pageYOffset - el.offsetHeight}px`
        // el.style.left = `${rect.left +
        //     window.pageXOffset -
        //     el.offsetWidth / 2 +
        //     rect.width / 2}px`
    }, [editor.selection, setFocused, editor])

    const [isActive, signalActivity] = useActivitySignal()

    const content = note?.slate.content || value!
    logger.inTestEnv('editor content:', content)

    return (
        <>
            <Slate
                editor={editor}
                value={content}
                onChange={action((newValue) => {
                    const { operations } = editor

                    logger.inTestEnv('processing change:', newValue)

                    const isASTChange = operations.some(
                        op => 'set_selection' !== op.type
                    )

                    if (note) {
                        /**
                         * Compute a quick list of changed nodes so we only change
                         * the content we care about (dummy optimization)
                         */
                        const changedBlocks = new Set<number>();

                        operations.forEach(op => {
                            // @ts-ignore
                            if (!!op.path) {
                                // @ts-ignore
                                changedBlocks.add(op.path[0])
                            }
                            // @ts-ignore
                            if (!!op.newPath) {
                                // @ts-ignore
                                changedBlocks.add(op.newPath[0])
                            }
                        })

                        note.slate.setContent(newValue, changedBlocks)
                    } else {
                        setValue!(newValue);
                    }
                    signalActivity()
                    autoComplete.onValueChange(isASTChange)
                })}
            >
                {/* {note?.slate.doc.hasFresh ? 'needs-refresh' : ''} */}
                <ActivityProvider value={isActive}>
                    <AutocompleteUI autocomplete={autoComplete} />
                    <Editable
                        placeholder="What's on your mind..."
                        className={`content ${!isActive ? 'is-inactive' : 'is-active'}`}
                        renderElement={renderElement}
                        renderLeaf={renderLeaf}
                        onKeyDown={(event) => {
                            // TODO:
                            // if (event.key === '[' || event.key === ']') {
                            //     const { selection } = editor;

                            //     if (selection && Range.isExpanded(selection)) {
                            //         let end = Range.end(selection)
                            //         end = { ...end, offset: end.offset + 1 }

                            //         Transforms.insertText(editor, '[', { at: Range.start(selection) })
                            //         Transforms.insertText(editor, ']', { at: end })
                            //         console.log('insert!')
                            //         event.preventDefault()
                            //         return;
                            //     }
                            // }
                            // if (event.key === '#') {
                            //     const { selection } = editor;

                            //     if (selection && Range.isExpanded(selection)) {
                            //         const start = Range.start(selection)

                            //         if (start.offset === 0) {
                            //             Transforms.insertText(editor, '#', { at: start })
                            //         }
                            //     }
                            // }
                            if (autoComplete.onKeyDown(event)) {
                                return;
                            }

                            if (isTestHotkey(event)) {
                                Transforms.mergeNodes(editor)
                            }

                            if (event.key === 'Enter') {
                                const [m] = Editor.nodes<IBlock>(editor, { match: (n, path) => isBlock(n) })
                                const s = Node.string(m[0])
                                if (s.length === 0) {
                                    Transforms.move(editor, { unit: 'line' })
                                    event.preventDefault();
                                }
                                return true;
                            }
                        }}
                    />
                </ActivityProvider>
            </Slate>
        </>
    );
});
